import { toast } from "sonner";
import { Mail, Instagram, Phone, Trash2 } from "lucide-react";
import { InlineText } from "./InlineText";
import { StatusCell } from "./StatusCell";
import { updateBookingDetails, deleteBooking } from "../../lib/api";
import { ARTISTS } from "../../lib/artists";

const ARTIST_OPTIONS = ARTISTS.filter((a) => a.role !== "Management");

export function ClientRow({ index, booking: b, artistId, onChanged, onDeleted }) {
  const save = async (payload) => {
    try {
      const updated = await updateBookingDetails(b.id, payload);
      onChanged(updated);
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${b.name}? This cannot be undone.`)) return;
    try {
      await deleteBooking(b.id);
      onDeleted(b.id);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const created = b.created_at ? new Date(b.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" }) : "";

  return (
    <div
      data-testid={`client-row-${b.id}`}
      className="group grid grid-cols-12 gap-x-4 gap-y-2 px-4 py-3 items-start text-sm border-b border-[#EFEFEF] hover:bg-[#FCFCFC] transition-colors"
    >
      <div className="col-span-12 md:col-span-3 flex gap-3 min-w-0">
        <span className="font-display text-base text-[#B3261E] w-6 shrink-0 pt-0.5" style={{ fontWeight: 300 }}>{index}.</span>
        <div className="min-w-0 flex-1">
          <InlineText value={b.name} onSave={(v) => v && save({ name: v })} testId={`client-name-${b.id}`} className="font-medium" />
          <div className="flex items-center gap-2 mt-0.5 text-[10px] tracking-[0.18em] uppercase text-[#999]">
            <span className="shrink-0 whitespace-nowrap">{created}</span>
            <span>·</span>
            <select
              value={b.preferred_artist}
              onChange={(e) => save({ artist: e.target.value })}
              data-testid={`client-artist-${b.id}`}
              className="shrink-0 appearance-none bg-transparent cursor-pointer outline-none uppercase tracking-[0.18em] text-[10px] hover:text-[#0A0A0A]"
              title="Move to another artist"
            >
              {!ARTIST_OPTIONS.some((a) => a.id === b.preferred_artist || a.name === b.preferred_artist) && (
                <option value={b.preferred_artist}>{b.preferred_artist || "Unassigned"}</option>
              )}
              {ARTIST_OPTIONS.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <span>·</span>
            <span className="min-w-0 flex-1">
              <InlineText value={b.source} onSave={(v) => v && save({ source: v })} testId={`client-source-${b.id}`} className="uppercase tracking-[0.18em] text-[10px]" placeholder="source" />
            </span>
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-3 space-y-0.5 text-xs text-[#444] min-w-0">
        <div className="flex items-center gap-2"><Mail size={11} className="shrink-0 text-[#999]" /><InlineText value={b.email} onSave={(v) => save({ email: v })} testId={`client-email-${b.id}`} placeholder="email" type="email" /></div>
        <div className="flex items-center gap-2"><Instagram size={11} className="shrink-0 text-[#999]" /><InlineText value={b.instagram} onSave={(v) => save({ instagram: v })} testId={`client-ig-${b.id}`} placeholder="instagram" /></div>
        <div className="flex items-center gap-2"><Phone size={11} className="shrink-0 text-[#999]" /><InlineText value={b.phone === "—" ? "" : b.phone} onSave={(v) => save({ phone: v || "—" })} testId={`client-phone-${b.id}`} placeholder="phone" /></div>
      </div>

      <div className="col-span-12 md:col-span-2">
        <StatusCell booking={b} artistId={artistId} onChanged={onChanged} />
      </div>

      <div className="col-span-11 md:col-span-3 text-xs text-[#444] min-w-0">
        <InlineText value={b.notes} onSave={(v) => save({ notes: v })} testId={`client-notes-${b.id}`} placeholder="+ add note" multiline />
      </div>

      <div className="col-span-1 flex justify-end">
        <button onClick={remove} data-testid={`client-delete-${b.id}`} className="p-1.5 text-[#CCC] hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
